import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend as RLegend } from "recharts";
import { formatDateTime } from "../utils";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Download, Eye, Search, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 10000;
const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#f97316"];

export default function Analytics() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOwner = !!user?.is_owner;
  const [data, setData] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [staffDrillUserId, setStaffDrillUserId] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/analytics/overview"); setData(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);

  if (!data) return <Layout title="Analytics"><div className="text-slate-400 text-sm">Loading analytics…</div></Layout>;
  const sc = data.scorecards;

  return (
    <Layout title="Analytics">
    <div className="space-y-6" data-testid="analytics-page">
      <h1 className="text-xl font-semibold tracking-tight">Analytics</h1>

      {/* Scorecards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <ScoreCard label="Total Candidates" value={sc.total_candidates} />
        <ScoreCard label="Total Leads" value={sc.total_leads} />
        <ScoreCard label="Total Clients" value={sc.total_clients} />
        <ScoreCard label="Active Staff Today" value={sc.active_staff_today} />
        <ScoreCard label="Present Today" value={sc.present_today ?? 0} />
        <ScoreCard label="Book Entries" value={sc.book_entries ?? 0} />
      </div>

      {/* Roster scorecards */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 num">Roster · today</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="roster-scorecards">
          <ScoreCard label="Total Workers" value={sc.roster_workers_total ?? 0} />
          <ScoreCard label="Attended" value={sc.roster_attended_today ?? 0} tone="emerald" />
          <ScoreCard label="Absent" value={sc.roster_absent_today ?? 0} tone="red" />
          <ScoreCard label="Unmarked" value={sc.roster_unmarked_today ?? 0} tone="slate" />
        </div>
      </div>

      {/* Calendar scorecards */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 num">Calendar · this week / this month</div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3" data-testid="calendar-scorecards">
          <ScoreCard label="Open · wk" value={data.calendar_week?.open ?? 0} />
          <ScoreCard label="Claimed · wk" value={data.calendar_week?.claimed ?? 0} tone="blue" />
          <ScoreCard label="Attended · wk" value={data.calendar_week?.attended ?? 0} tone="emerald" />
          <ScoreCard label="Missed · wk" value={data.calendar_week?.missed ?? 0} tone="red" />
          <ScoreCard label="Open · mo" value={data.calendar_month?.open ?? 0} />
          <ScoreCard label="Claimed · mo" value={data.calendar_month?.claimed ?? 0} tone="blue" />
          <ScoreCard label="Attended · mo" value={data.calendar_month?.attended ?? 0} tone="emerald" />
          <ScoreCard label="Missed · mo" value={data.calendar_month?.missed ?? 0} tone="red" />
        </div>
      </div>

      {/* Roster 30-day trend + absenteeism top10 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <ChartCard title="Roster attendance · 30-day trend">
            {(data.roster_trend_30d || []).length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-10">No roster data in the last 30 days.</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.roster_trend_30d}>
                  <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <RLegend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
                  <Line type="monotone" dataKey="attended" stroke="#10b981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="absent" stroke="#ef4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
        <ChartCard title="Top 10 absentee companies · this month">
          {(data.roster_absent_top10 || []).length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-10">No absences recorded.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.roster_absent_top10} layout="vertical">
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={110} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#ef4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Claim leaderboard */}
      <ChartCard title="Appointment claim leaderboard · this month">
        {(data.claim_leaderboard || []).length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">No appointments claimed yet this month.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="claim-leaderboard">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b hairline">
                  <th className="py-2 px-2">Staff</th>
                  <th className="px-2 num">Claimed</th>
                  <th className="px-2 num">Attended</th>
                  <th className="px-2 num">Missed</th>
                  <th className="px-2 num">Attended rate</th>
                </tr>
              </thead>
              <tbody>
                {data.claim_leaderboard.map((s, i) => (
                  <tr key={s.name + i} className="border-b border-slate-900/80">
                    <td className="py-2 px-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                      {s.name}
                    </td>
                    <td className="px-2 num">{s.claimed}</td>
                    <td className="px-2 num text-emerald-400">{s.attended}</td>
                    <td className="px-2 num text-red-400">{s.missed}</td>
                    <td className="px-2 num">
                      {s.attended_rate == null ? (
                        <span className="text-slate-500">—</span>
                      ) : (
                        <span className={s.attended_rate >= 70 ? "text-emerald-400" : s.attended_rate >= 40 ? "text-amber-300" : "text-red-400"}>
                          {s.attended_rate}%
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>

      {/* Row 2: Candidates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ChartCard title="Candidates by Status">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.candidates_by_status} dataKey="count" nameKey="name" outerRadius={80} innerRadius={50} paddingAngle={2}>
                {data.candidates_by_status.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <Legend items={data.candidates_by_status} />
        </ChartCard>

        <ChartCard title="Candidates by Staff">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.candidates_by_staff}>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {data.candidates_by_staff.map((s, i) => <Cell key={i} fill={s.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Candidates by Country">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.candidates_by_country} layout="vertical">
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} width={60} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Funnel */}
      <ChartCard title="Candidate Funnel">
        <div className="flex items-stretch gap-2">
          {data.candidates_funnel.map((s, i) => (
            <div key={s.name} className="flex-1 text-center">
              <div className="rounded-md py-4 border hairline surface-2">
                <div className="text-2xl font-semibold num">{s.count}</div>
                <div className="text-xs text-slate-400 mt-1">{s.name}</div>
              </div>
              {i < data.candidates_funnel.length - 1 && <div className="text-slate-600 mt-2">↓</div>}
            </div>
          ))}
        </div>
      </ChartCard>

      {/* Row 3: Leads */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ChartCard title="Leads Pipeline">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.leads_pipeline}>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Lead Source Breakdown">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.leads_by_source} dataKey="count" nameKey="name" outerRadius={80} innerRadius={50}>
                {data.leads_by_source.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <Legend items={data.leads_by_source} />
        </ChartCard>

        <ChartCard title="Win Rate by Staff">
          {data.win_rate_by_staff.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-10">No closed deals yet.</div>
          ) : (
            <div className="space-y-2">
              {data.win_rate_by_staff.map((s) => (
                <div key={s.name} className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                  <span className="text-sm w-28 truncate">{s.name}</span>
                  <div className="flex-1 h-1.5 bg-slate-800 rounded overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${s.rate}%` }} />
                  </div>
                  <span className="text-xs num text-slate-400 w-14 text-right">{s.rate}%</span>
                </div>
              ))}
            </div>
          )}
        </ChartCard>
      </div>

      {/* Row 4: Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <ChartCard
            title="Activity Feed"
            actions={isOwner ? (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs gap-1.5"
                onClick={() => setExportOpen(true)}
                data-testid="activity-export-btn"
              >
                <Download className="w-3.5 h-3.5" /> Export CSV
              </Button>
            ) : null}
          >
            <div className="max-h-[420px] overflow-y-auto scrollbar-thin" data-testid="activity-feed">
              {data.activity_feed.map((a) => (
                <div key={a.id} className="flex items-start gap-3 py-2 border-b border-slate-900">
                  <span className="w-2 h-2 mt-1.5 rounded-full shrink-0" style={{ background: a.user_color }} />
                  <div className="flex-1 text-sm">
                    <span className="font-medium">{a.user_name}</span>
                    <span className="text-slate-400"> {a.action.replace(/_/g, " ")} </span>
                    <span className="text-slate-300">{a.entity_label || a.entity_type}</span>
                  </div>
                  <span className="text-xs num text-slate-500 whitespace-nowrap">{formatDateTime(a.timestamp)}</span>
                </div>
              ))}
              {data.activity_feed.length === 0 && (
                <div className="text-xs text-slate-500 text-center py-10">No activity yet.</div>
              )}
            </div>
          </ChartCard>
        </div>

        <div className="space-y-3">
          <ChartCard title="Leaderboard (24h)">
            {data.leaderboard.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6">No activity recorded.</div>
            ) : (
              <div className="space-y-2">
                {data.leaderboard.map((l, i) => (
                  <div key={l.name + i} className="flex items-center gap-3">
                    <span className="num text-slate-500 w-6">{i + 1}.</span>
                    <span className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                    <span className="text-sm flex-1 truncate">{l.name}</span>
                    <span className="text-xs num text-slate-400">{l.count}</span>
                  </div>
                ))}
              </div>
            )}
          </ChartCard>

          <ChartCard title="Stale Unclaimed Candidates">
            {data.stale_candidates.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6">All candidates are claimed.</div>
            ) : (
              <div className="space-y-1.5">
                {data.stale_candidates.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-xs num text-slate-500">{formatDateTime(c.date_added)}</span>
                  </div>
                ))}
              </div>
            )}
          </ChartCard>
        </div>
      </div>

      {isOwner && <ActivityExportDialog open={exportOpen} onOpenChange={setExportOpen} />}
      {isOwner && (
        <StaffActivityPanel
          onOpenDrill={(uid) => setStaffDrillUserId(uid)}
        />
      )}
      {isOwner && staffDrillUserId && (
        <StaffActivityDrillDialog
          userId={staffDrillUserId}
          onClose={() => setStaffDrillUserId(null)}
        />
      )}
    </div>
    </Layout>
  );
}

function ActivityExportDialog({ open, onOpenChange }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      if (action.trim()) params.set("action", action.trim());
      if (entityType.trim()) params.set("entity_type", entityType.trim());
      const res = await api.get(`/activity/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `activity-log_${from || "all"}_to_${to || today}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md" data-testid="activity-export-dialog">
        <DialogHeader>
          <DialogTitle>Export activity log (CSV)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-400">
            Filters are optional. Leave blank to export everything.
            CSV is UTF-8 with BOM so Excel renders accents correctly.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="export-date-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="export-date-to" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Action (optional)</Label>
            <Input value={action} onChange={(e) => setAction(e.target.value)}
              placeholder="e.g. login, candidate_created"
              className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="export-action" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Entity type (optional)</Label>
            <Input value={entityType} onChange={(e) => setEntityType(e.target.value)}
              placeholder="e.g. user, candidate, lead, client"
              className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="export-entity-type" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="export-submit-btn">
            {busy ? "Exporting…" : "Download CSV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const tooltipStyle = { background: "#111827", border: "1px solid #1f2937", borderRadius: 6, color: "#f1f5f9", fontSize: 12 };

// ====================================================================
// Staff Activity — owner-only audit drill-down (90-day retention)
// ====================================================================
function StaffActivityPanel({ onOpenDrill }) {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [retentionDays, setRetentionDays] = useState(90);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/activity/staff-summary?days=${days}`)
      .then(({ data }) => { if (!alive) return; setRows(data.rows); setRetentionDays(data.retention_days); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  return (
    <div className="rounded-lg border hairline surface-1 p-4" data-testid="staff-activity-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="text-sm font-medium text-slate-200">Staff Activity</div>
          <div className="text-[10px] text-slate-500 num">
            Owner-only audit trail · auto-purges after {retentionDays} days
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {[7, 30, 90].map((d) => (
            <button key={d}
              onClick={() => setDays(d)}
              data-testid={`staff-act-window-${d}`}
              className={`px-2 py-1 rounded text-[11px] font-medium num transition ${
                days === d
                  ? "bg-blue-500/20 text-blue-200 border border-blue-500/40"
                  : "bg-slate-800/40 text-slate-400 border border-slate-800 hover:text-slate-200"
              }`}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-slate-500 py-8 text-center">Loading…</div>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 num">
                <th className="text-left py-2 px-2">Staff</th>
                <th className="text-right py-2 px-2 hidden sm:table-cell">Logins</th>
                <th className="text-right py-2 px-2">Actions</th>
                <th className="text-left py-2 px-2 hidden md:table-cell">Top module</th>
                <th className="text-left py-2 px-2 hidden md:table-cell">Last action</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id}
                    className="border-t border-slate-900 hover:bg-slate-800/30 cursor-pointer"
                    onClick={() => onOpenDrill(r.user_id)}
                    data-testid={`staff-act-row-${r.username}`}>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: r.user_color }} />
                      <div className="min-w-0">
                        <div className="text-slate-200 truncate">{r.user_name}
                          {r.role === "admin" && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-amber-400 num">admin</span>}
                        </div>
                        <div className="text-[10px] text-slate-600 num truncate sm:hidden">
                          {r.logins} logins · {r.top_module || "—"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="text-right py-2 px-2 num text-slate-400 hidden sm:table-cell">{r.logins}</td>
                  <td className="text-right py-2 px-2 num font-medium text-slate-100">{r.total}</td>
                  <td className="py-2 px-2 text-slate-400 hidden md:table-cell">
                    {r.top_module ? (
                      <span className="text-[11px] num">{r.top_module} <span className="text-slate-600">· {r.top_module_n}</span></span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="py-2 px-2 text-slate-400 hidden md:table-cell">
                    {r.last_action_at ? (
                      <div className="text-[11px] num">
                        <div>{r.last_action?.replace(/_/g, " ")}</div>
                        <div className="text-slate-600">{formatDateTime(r.last_action_at)}</div>
                      </div>
                    ) : <span className="text-slate-600">no activity</span>}
                  </td>
                  <td className="py-2 px-2 text-right">
                    <ChevronRight className="w-4 h-4 text-slate-500 inline-block" />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-xs text-slate-500">No staff activity in window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StaffActivityDrillDialog({ userId, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 100;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset });
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      if (action) params.set("action", action);
      if (entityType) params.set("entity_type", entityType);
      if (search.trim()) params.set("q", search.trim());
      const { data } = await api.get(`/activity/user/${userId}?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [userId, from, to, action, entityType, search, offset]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams({ user_id: userId });
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      if (action) params.set("action", action);
      if (entityType) params.set("entity_type", entityType);
      const res = await api.get(`/activity/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `staff-activity_${userId.slice(0, 8)}_${from || "all"}_to_${to || today}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  // Group rows by day
  const grouped = (data?.rows || []).reduce((acc, r) => {
    const day = (r.timestamp || "").slice(0, 10);
    if (!acc[day]) acc[day] = [];
    acc[day].push(r);
    return acc;
  }, {});
  const sortedDays = Object.keys(grouped).sort().reverse();

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="staff-activity-drill">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-800">
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-blue-400" />
            Staff activity drill-down
          </DialogTitle>
          <div className="text-[10px] text-slate-500 num mt-0.5">
            Owner-only view · last 90 days are auto-purged after the retention window
          </div>
        </DialogHeader>

        {/* Sparkline strip */}
        {data && (
          <div className="px-5 py-3 border-b border-slate-800 bg-slate-900/30">
            <div className="flex items-baseline gap-3 mb-2">
              <div className="text-2xl font-semibold num">{data.total}</div>
              <div className="text-xs text-slate-500 num">actions in current filter</div>
            </div>
            <div className="flex items-end gap-0.5 h-10">
              {data.histogram_30d.map((d, i) => {
                const max = Math.max(1, ...data.histogram_30d.map(x => x.count));
                const pct = (d.count / max) * 100;
                return (
                  <div key={d.day} className="flex-1 bg-blue-500/40 hover:bg-blue-400/70 rounded-sm"
                    style={{ height: `${pct}%`, minHeight: 2 }}
                    title={`${d.day}: ${d.count} actions`} />
                );
              })}
              {data.histogram_30d.length === 0 && (
                <div className="text-[10px] text-slate-600 num w-full text-center self-center">No data</div>
              )}
            </div>
            <div className="flex justify-between text-[10px] text-slate-600 num mt-1">
              <span>{data.histogram_30d[0]?.day || "—"}</span>
              <span>last 30 days</span>
              <span>{data.histogram_30d[data.histogram_30d.length - 1]?.day || "—"}</span>
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="px-5 py-3 border-b border-slate-800 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">From</Label>
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setOffset(0); }}
              className="bg-[#0a0e1a] border-slate-800 mt-1 h-8 text-xs" data-testid="drill-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">To</Label>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setOffset(0); }}
              className="bg-[#0a0e1a] border-slate-800 mt-1 h-8 text-xs" data-testid="drill-to" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Action</Label>
            <select value={action} onChange={(e) => { setAction(e.target.value); setOffset(0); }}
              className="w-full bg-[#0a0e1a] border border-slate-800 rounded mt-1 h-8 text-xs px-2"
              data-testid="drill-action">
              <option value="">all</option>
              {(data?.actions || []).map((a) => (
                <option key={a} value={a}>{a.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Module</Label>
            <select value={entityType} onChange={(e) => { setEntityType(e.target.value); setOffset(0); }}
              className="w-full bg-[#0a0e1a] border border-slate-800 rounded mt-1 h-8 text-xs px-2"
              data-testid="drill-entity-type">
              <option value="">all</option>
              {(data?.entity_types || []).map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Search</Label>
            <div className="relative mt-1">
              <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input value={search}
                onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
                placeholder="name, label…"
                className="bg-[#0a0e1a] border-slate-800 h-8 text-xs pl-7" data-testid="drill-search" />
            </div>
          </div>
        </div>

        {/* Timeline grouped by day */}
        <div className="overflow-y-auto flex-1 px-5 py-3" data-testid="drill-timeline">
          {loading && <div className="text-xs text-slate-500 py-8 text-center">Loading…</div>}
          {!loading && sortedDays.length === 0 && (
            <div className="text-xs text-slate-500 py-12 text-center">No matching activity.</div>
          )}
          {!loading && sortedDays.map((day) => (
            <div key={day} className="mb-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 num mb-1.5 sticky top-0 bg-[#0f1626] py-1">
                {day} <span className="text-slate-700">· {grouped[day].length} actions</span>
              </div>
              <div className="space-y-1 pl-2 border-l border-slate-800">
                {grouped[day].map((r) => (
                  <div key={r.id} className="flex items-start gap-2 py-1 text-xs hover:bg-slate-800/20 rounded px-2 -ml-2">
                    <span className="text-slate-600 num shrink-0 w-12">{r.timestamp.slice(11, 19)}</span>
                    <span className="text-blue-300 num shrink-0">{r.action.replace(/_/g, " ")}</span>
                    <span className="text-slate-500 shrink-0">·</span>
                    <span className="text-slate-400 shrink-0">{r.entity_type}</span>
                    {r.entity_label && (
                      <>
                        <span className="text-slate-600 shrink-0">·</span>
                        <span className="text-slate-300 truncate">{r.entity_label}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {data && data.total > data.rows.length + offset && (
            <div className="text-center py-3">
              <Button size="sm" variant="outline" onClick={() => setOffset(offset + LIMIT)}
                data-testid="drill-load-more">
                Load more ({data.total - data.rows.length - offset} remaining)
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800 flex items-center justify-between gap-2">
          <div className="text-[10px] text-slate-500 num">
            Showing {data?.rows.length || 0} of {data?.total || 0} matching events
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={exportCSV} data-testid="drill-export-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="drill-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScoreCard({ label, value, tone }) {
  const toneCls = {
    emerald: "text-emerald-300",
    red: "text-red-300",
    blue: "text-blue-300",
    slate: "text-slate-300",
  }[tone] || "text-slate-100";
  return (
    <div className="rounded-lg border hairline surface-1 p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 num">{label}</div>
      <div className={`text-3xl font-semibold num mt-1 tabular-nums ${toneCls}`}>{value ?? 0}</div>
    </div>
  );
}

function ChartCard({ title, children, actions }) {
  return (
    <div className="rounded-lg border hairline surface-1 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-slate-200">{title}</div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

function Legend({ items }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs">
      {items.map((it, i) => (
        <span key={it.name + i} className="inline-flex items-center gap-1.5 text-slate-400">
          <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
          <span>{it.name}</span>
          <span className="num text-slate-500">· {it.count}</span>
        </span>
      ))}
    </div>
  );
}
